import { useCallback, useEffect, useMemo, useRef } from "react";

import { AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { useLazyGetCompaniesQuery } from "@/pages/Private/redux/companies/companies.api";

export const COMPANY_SEARCH_LIMIT = 20;
export const COMPANY_SEARCH_DEBOUNCE_MS = 300;

/**
 * A debounced `GET company?searchValue=` (GLOBAL_ADMIN) for the company pickers — the queue's
 * company filter and the staff new-ticket modal. One request per pause, never per keystroke.
 */
export const useCompanySearch = () => {
	const [trigger, { data, isFetching }] = useLazyGetCompaniesQuery();
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(
		() => () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		},
		[]
	);

	const search = useCallback(
		(value: string) => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}

			timerRef.current = setTimeout(() => {
				trigger({ page: 1, limit: COMPANY_SEARCH_LIMIT, searchValue: value.trim() || undefined });
			}, COMPANY_SEARCH_DEBOUNCE_MS);
		},
		[trigger]
	);

	const options: AutoCompleteItem[] = useMemo(
		() => (data?.data ?? []).map((company) => ({ id: company.id, title: company.name })),
		[data]
	);

	return { search, options, isLoading: isFetching };
};
